import { Box, HoverCard, Link } from '@workos-inc/design-system';
import get from 'lodash.get';
import NextLink from 'next/link';
import * as React from 'react';
import { isExternalUrl } from 'src/utils/is-external-url';
import { ReferenceEntity, ReferenceLanguage } from 'src/utils/reference-schema';
import { useReferenceData } from './reference-data-provider';
import { TypeDefinitionContext } from './type-definition-context';
import { TypeDefinitionEntity } from './type-definition-entity';
interface CodeBlockHoverCardProps {
  children?: React.ReactNode;
  language?: ReferenceLanguage;
  /** Type definition declaration when there is no corresponding class, e.g. `"const organizationList".` */
  declaration?: string;
  /** Defautl URL to use when the Reference Entity doesn't have a URL property. */
  defaultHref?: string;
  /** Reference ID or path, e.g. "list_organizations", "list_organizations.returns" */
  referenceId?: string;
}
export const CodeBlockHoverCard = ({
  children,
  declaration,
  defaultHref = '/reference',
  language = 'curl',
  referenceId
}: CodeBlockHoverCardProps) => {
  const reference = useReferenceData(language);
  if (!referenceId || !reference) {
    return null;
  }
  const entity = getEntity(referenceId, reference);
  if (!entity) {
    return <>{children}</>;
  }
  const url = entity.url ?? defaultHref;
  return <HoverCard.Root data-sentry-element="HoverCard.Root" data-sentry-component="CodeBlockHoverCard" data-sentry-source-file="code-block-hover-card.tsx">
      <HoverCard.Content side="top" sideOffset={1} size="1" data-sentry-element="HoverCard.Content" data-sentry-source-file="code-block-hover-card.tsx">
        <Box my="-1" data-sentry-element="Box" data-sentry-source-file="code-block-hover-card.tsx">
          <code className="CodeBlockCode">
            <pre className="rt-reset">
              <TypeDefinitionContext.Provider value={{
              declaration,
              entity,
              expanded: true,
              language,
              passHrefs: true
            }} data-sentry-element="TypeDefinitionContext.Provider" data-sentry-source-file="code-block-hover-card.tsx">
                <TypeDefinitionEntity data-sentry-element="TypeDefinitionEntity" data-sentry-source-file="code-block-hover-card.tsx" />
              </TypeDefinitionContext.Provider>
            </pre>
          </code>
        </Box>
      </HoverCard.Content>
      <HoverCard.Trigger data-sentry-element="HoverCard.Trigger" data-sentry-source-file="code-block-hover-card.tsx">
        <Link asChild data-sentry-element="Link" data-sentry-source-file="code-block-hover-card.tsx">
          <NextLink href={url} target={isExternalUrl(url) ? '_blank' : undefined} data-sentry-element="NextLink" data-sentry-source-file="code-block-hover-card.tsx">
            {children}
          </NextLink>
        </Link>
      </HoverCard.Trigger>
    </HoverCard.Root>;
};

/** Get entity by id or path, e.g. "list_organizations" or "list_organizations.returns" */
const getEntity = (referenceId: string, reference: ReferenceEntity[]): ReferenceEntity | undefined => {
  if (referenceId.includes('.')) {
    const [parentId, path = ''] = referenceId.split(/\.(.*)/);
    const parent = reference?.find(({
      id
    }) => id === parentId);
    return get(parent, path);
  }
  return reference.find(({
    id
  }) => id === referenceId);
};